/**
 * EditorPortalPanel: configures a portal object's teleport target.
 *
 * Mounted inside the property panel only when the selected object is a portal.
 * Edits `SceneObject.portal` (targetScene / requireConfirm / confirmText /
 * color) and offers a 测试传送 convenience. The target scene manifest owns the
 * arrival spawn and angles.
 */

import type { SceneObject, PortalConfig } from '../scene/SceneObjectTypes';
import type { EditorState } from './EditorState';
import type { SceneObjectManager } from '../scene/SceneObjectManager';

export interface PortalPanelMutators {
    mutate(id: string, fn: (o: SceneObject) => void): void;
    /** Rebuild the marker entity (so a color edit shows immediately). */
    refresh(id: string): void;
    changed(): void;
    getScenes(): string[];
    testPortal(id: string): void;
}

export class EditorPortalPanel {
    private _mount: HTMLElement;
    private _state: EditorState;
    private _getMgr: () => SceneObjectManager | null;
    private _mut: PortalPanelMutators;

    constructor(
        mount: HTMLElement,
        state: EditorState,
        getMgr: () => SceneObjectManager | null,
        mut: PortalPanelMutators
    ) {
        this._mount = mount;
        this._state = state;
        this._getMgr = getMgr;
        this._mut = mut;
        this._render();
    }

    render(): void { this._render(); }

    private _render(): void {
        const id = this._state.selectedId;
        const o = id ? this._getMgr()?.get(id) : undefined;
        this._mount.innerHTML = '';

        if (!id || !o || o.type !== 'portal' || !o.portal) {
            const empty = document.createElement('div');
            empty.className = 'ep-empty';
            empty.textContent = '非传送门对象';
            this._mount.appendChild(empty);
            return;
        }

        const portal = o.portal;
        const set = (fn: (p: PortalConfig) => void) => {
            this._mut.mutate(o.id, (x) => { if (x.portal) fn(x.portal); });
        };

        // Target scene dropdown.
        const sceneSel = document.createElement('select');
        sceneSel.className = 'ev-type ep-grow';
        const blank = document.createElement('option');
        blank.value = ''; blank.textContent = '— 选择目标场景 —';
        if (!portal.targetScene) blank.selected = true;
        sceneSel.appendChild(blank);
        for (const sid of this._mut.getScenes()) {
            const opt = document.createElement('option');
            opt.value = sid; opt.textContent = sid;
            if (sid === portal.targetScene) opt.selected = true;
            sceneSel.appendChild(opt);
        }
        sceneSel.addEventListener('change', () => {
            set((p) => { p.targetScene = sceneSel.value; });
            this._mut.changed();
        });
        this._mount.appendChild(this._row('目标场景', sceneSel));
        this._mount.appendChild(this._row('目标房间 ID', this._text(portal.targetRoomId ?? '', value => {
            set(p => { p.targetRoomId = value.trim(); }); this._mut.changed();
        })));

        // 触发半径（球形）：玩家进入此半径内即触发传送。
        // 运行时由 SceneObjectManager._isInside 读取 interaction.radius（默认 3）。
        const radiusInp = document.createElement('input');
        radiusInp.type = 'number';
        radiusInp.className = 'ep-input';
        radiusInp.step = '0.1';
        radiusInp.min = '0.1';
        radiusInp.value = String(+(o.interaction?.radius ?? 3).toFixed(3));
        radiusInp.addEventListener('input', () => {
            const v = parseFloat(radiusInp.value);
            if (Number.isFinite(v) && v > 0) {
                this._mut.mutate(o.id, (x) => {
                    if (!x.interaction) x.interaction = { mode: 'enter', events: [] };
                    x.interaction.radius = v;
                });
                // 同步重建场景中的触发范围圆球（EditorSelection.sel-range）。
                this._mut.refresh(o.id);
                this._mut.changed();
            }
        });
        this._mount.appendChild(this._row('触发半径', radiusInp));

        // requireConfirm + confirmText.
        const confirmBox = document.createElement('label');
        confirmBox.className = 'ev-check';
        const cb = document.createElement('input');
        cb.type = 'checkbox'; cb.checked = portal.requireConfirm ?? false;
        cb.addEventListener('change', () => { set((p) => { p.requireConfirm = cb.checked; }); this._mut.changed(); });
        confirmBox.appendChild(cb);
        confirmBox.append(' 需要确认');
        this._mount.appendChild(confirmBox);

        this._mount.appendChild(this._row('确认提示语', this._text(portal.confirmText ?? '', (v) => set((p) => { p.confirmText = v; }))));

        // Color.
        this._mount.appendChild(this._vecRow('颜色 R/G/B', portal.color ?? [1, 0.85, 0.3], 0.01, (v) => {
            set((p) => { p.color = v; });
            // Also tint the marker so the designer sees the new color immediately.
            this._mut.mutate(o.id, (x) => { if (x.marker.kind === 'primitive') x.marker.color = v; });
            this._mut.refresh(o.id);
            this._mut.changed();
        }));

        // Action: test the portal (fire PORTAL_ENTER for this object).
        const actions = document.createElement('div');
        actions.className = 'ev-actions';
        actions.appendChild(this._btn('测试传送', () => this._mut.testPortal(o.id), 'ev-test'));
        this._mount.appendChild(actions);
    }

    // ── DOM helpers ──
    private _row(label: string, field: HTMLElement): HTMLElement {
        const r = document.createElement('div');
        r.className = 'ev-row';
        const l = document.createElement('span');
        l.className = 'ep-label'; l.textContent = label;
        r.appendChild(l); r.appendChild(field);
        return r;
    }

    private _vecRow(label: string, value: [number, number, number], on: (v: [number, number, number]) => void): HTMLElement;
    private _vecRow(label: string, value: [number, number, number], step: number, on: (v: [number, number, number]) => void): HTMLElement;
    private _vecRow(label: string, value: [number, number, number], stepOrOn: number | ((v: [number, number, number]) => void), onMaybe?: (v: [number, number, number]) => void): HTMLElement {
        const step = typeof stepOrOn === 'number' ? stepOrOn : 0.5;
        const on = typeof stepOrOn === 'function' ? stepOrOn : onMaybe!;
        const wrap = document.createElement('div');
        wrap.className = 'ep-vec3';
        const cur = [value[0], value[1], value[2]] as [number, number, number];
        for (let k = 0; k < 3; k++) {
            const idx = k;
            const inp = document.createElement('input');
            inp.type = 'number'; inp.value = String(+value[k].toFixed(3)); inp.step = String(step);
            inp.className = 'ep-input';
            inp.addEventListener('input', () => {
                const v = parseFloat(inp.value);
                if (Number.isFinite(v)) { cur[idx] = v; on([cur[0], cur[1], cur[2]]); }
            });
            wrap.appendChild(inp);
        }
        return this._row(label, wrap);
    }

    private _text(value: string, on: (v: string) => void): HTMLInputElement {
        const i = document.createElement('input');
        i.type = 'text'; i.value = value; i.className = 'ep-input ep-grow';
        i.addEventListener('input', () => on(i.value));
        return i;
    }

    private _btn(label: string, on: () => void, cls = ''): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = `etb-btn ${cls}`.trim();
        b.textContent = label;
        b.addEventListener('click', on);
        return b;
    }

    destroy(): void { this._mount.innerHTML = ''; }
}
